import React, {useEffect, useMemo, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {useTranslation} from 'react-i18next';
import ActionUiHandler from "@ui/template/actionUiHandler/ActionUiHandler";
import SpinnerCustom from "@ui/spinner/SpinnerCustom";
import ButtonCustom from "@ui/buttons/buttonCustom/ButtonCustom";
import PrimaryButtonSingleVoyage
	from "@containers/singleVoyage/ui/buttonts/primaryButtonSingleVoyage/PrimaryButtonSingleVoyage";
import {useGetByIdSingleVoyageQuery} from "@containers/singleVoyage/fetchingData/queryHooks/useSingleVoyagesQuery";
import {
	useGetSyncPreviewVoyageTemplateQuery,
	useGetVoyageTemplateByIdSingleVoyageQuery,
	useSyncVoyagesVoyageTemplateQuery
} from "@containers/singleVoyage/voyageTemplates/queryHooks/useVoyageTemplatesSingleVoyageQuery";
import {ISyncItemVoyageTemplate} from "@containers/singleVoyage/voyageTemplates/types";
import {TONE_SYNC_OUTCOME} from "@containers/singleVoyage/voyageTemplates/sync/constants";
import CardSyncVoyageTemplate from "@containers/singleVoyage/voyageTemplates/sync/card/CardSyncVoyageTemplate";
import DetailSyncVoyageTemplate from "@containers/singleVoyage/voyageTemplates/sync/detail/DetailSyncVoyageTemplate";
import WhitePaperSync, {
	WHITE_PAPER_CLASS_SYNC
} from "@containers/singleVoyage/voyageTemplates/sync/WhitePaperSync";
import {sx} from './sx'

interface IProps {
	templateId: number
	accountId: string | number
	closeModalFn?: () => void
}

/**
 * Offers to bring the template's planned voyages in line with it. Clean voyages are preselected;
 * a voyage edited by hand is only overwritten when the user ticks it, everything else is shown
 * with the reason it cannot be synced. The selected card drives the detail pane on the right.
 */
const SyncVoyagesVoyageTemplate = ({templateId, accountId, closeModalFn}: IProps) => {

	const {t} = useTranslation()

	const {
		data: items,
		isLoading,
	} = useGetSyncPreviewVoyageTemplateQuery({
		params: {
			accountId,
			templateId,
		}
	})

	const {
		data: template,
		isLoading: isLoadingTemplate,
	} = useGetVoyageTemplateByIdSingleVoyageQuery({
		params: {
			accountId,
			id: templateId,
		}
	})

	const [selected, setSelected] = useState<number[]>([])
	const [activeId, setActiveId] = useState<number | null>(null)
	const [results, setResults] = useState<Record<number, ISyncItemVoyageTemplate> | null>(null)

	useEffect(() => {
		setSelected(items?.filter((item) => item.outcome === 'READY').map((item) => item.voyageId) ?? [])
		setActiveId(items?.[0]?.voyageId ?? null)
	}, [items])

	const {data: activeVoyage} = useGetByIdSingleVoyageQuery({
		params: {
			voyageId: activeId,
			accountId,
		}
	})

	const {
		mutate: sync,
		isPending,
	} = useSyncVoyagesVoyageTemplateQuery({
		onSuccess: (synced) => {
			setResults(Object.fromEntries(synced.map((item) => [item.voyageId, item])))
		}
	})

	const forced = useMemo(() => {
		return selected.filter((id) => items?.find((item) => item.voyageId === id)?.outcome === 'DIVERGED')
	}, [selected, items])

	const summary = useMemo(() => {
		const counts = {ok: 0, warn: 0, fail: 0}
		items?.forEach((item) => {
			counts[TONE_SYNC_OUTCOME[results?.[item.voyageId]?.outcome ?? item.outcome]]++
		})
		return counts
	}, [items, results])

	const active = items?.find((item) => item.voyageId === activeId) ?? null

	const onToggle = (voyageId: number) => {
		setSelected((prev) => prev.includes(voyageId)
			? prev.filter((id) => id !== voyageId)
			: [...prev, voyageId])
	}

	const onApply = () => {
		sync({
			accountId,
			templateId,
			voyageIds: selected,
			forcedVoyageIds: forced,
		})
	}

	// the window keeps its final size and white surface while loading, so it does not jump when the data comes
	if (isLoading || isLoadingTemplate) {
		return (
			<Stack sx={[sx.root, sx.loading]} className={WHITE_PAPER_CLASS_SYNC}>
				<WhitePaperSync/>
				<SpinnerCustom variant={'absolute'}/>
			</Stack>
		)
	}

	if (!items?.length) {
		return (
			<Stack sx={sx.empty} className={WHITE_PAPER_CLASS_SYNC}>
				<WhitePaperSync/>
				<TaskAltIcon sx={sx.emptyIcon}/>
				<Typography sx={sx.emptyTitle}>
					{t('singleVoyage.voyageTemplate.sync.noVoyages')}
				</Typography>
				<ButtonCustom onClick={closeModalFn}>
					{t('singleVoyage.voyageTemplate.sync.close')}
				</ButtonCustom>
			</Stack>
		)
	}

	return (
		<ActionUiHandler
			sx={sx.root}
			isLoading={isPending}
			className={WHITE_PAPER_CLASS_SYNC}
		>
			<WhitePaperSync/>
			{/* actions sit up here, so the map and the voyage list get the height the footer took */}
			<Stack sx={sx.top}>
				<Stack sx={sx.header}>
					<Stack sx={sx.summary}>
						<Typography sx={sx.summaryTitle}>
							{t('singleVoyage.voyageTemplate.sync.voyages', {count: items?.length ?? 0})}
						</Typography>

						{!!summary.ok && <Box sx={[sx.summaryChip, sx.ok]}>
							{t(results ? 'singleVoyage.voyageTemplate.sync.summary.updated' : 'singleVoyage.voyageTemplate.sync.summary.ready', {count: summary.ok})}
						</Box>}
						{!!summary.warn && <Box sx={[sx.summaryChip, sx.warn]}>
							{t('singleVoyage.voyageTemplate.sync.summary.diverged', {count: summary.warn})}
						</Box>}
						{!!summary.fail && <Box sx={[sx.summaryChip, sx.fail]}>
							{t('singleVoyage.voyageTemplate.sync.summary.skipped', {count: summary.fail})}
						</Box>}
					</Stack>

					<Typography sx={sx.hint}>
						{t('singleVoyage.voyageTemplate.sync.hint')}
					</Typography>
				</Stack>

				<Stack sx={sx.topActions}>
					<Stack sx={sx.actions}>
						<ButtonCustom onClick={closeModalFn}>
							{results
								? t('singleVoyage.voyageTemplate.sync.close')
								: t('singleVoyage.voyageTemplate.sync.later')}
						</ButtonCustom>

						{!results && <PrimaryButtonSingleVoyage
							onClick={onApply}
							disabled={!selected.length || isPending}
						>
							{t('singleVoyage.voyageTemplate.sync.apply', {count: selected.length})}
						</PrimaryButtonSingleVoyage>}
					</Stack>

					{!results && !!forced.length && <Typography sx={sx.warning}>
						{t('singleVoyage.voyageTemplate.sync.forceWarning')}
					</Typography>}
				</Stack>
			</Stack>

			<Box sx={sx.body}>
				<Stack sx={sx.list}>
					{items?.map((item) => (
						<CardSyncVoyageTemplate
							key={item.voyageId}
							item={item}
							result={results?.[item.voyageId]}
							checked={selected.includes(item.voyageId)}
							selectable={item.outcome === 'READY' || item.outcome === 'DIVERGED'}
							active={item.voyageId === activeId}
							onToggle={() => onToggle(item.voyageId)}
							onSelect={() => setActiveId(item.voyageId)}
						/>
					))}
				</Stack>

				{/* keyed by voyage so switching cards starts the pane from the top */}
				<Box sx={sx.detail} key={activeId}>
					{active && template && <DetailSyncVoyageTemplate
                        item={active}
                        template={template}
                        voyage={activeVoyage ?? null}
                    />}
				</Box>
			</Box>

		</ActionUiHandler>
	);
};

export default SyncVoyagesVoyageTemplate;
